import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Download, Share2, Loader2, ShieldCheck, AlertCircle, Sparkles, Calendar } from "lucide-react";
import { api } from "../lib/config";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

function formatDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("es-US", { day: "2-digit", month: "long", year: "numeric" });
  } catch { return iso; }
}

export default function CouponView() {
  const { code } = useParams();
  const nav = useNavigate();
  const [coupon, setCoupon] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [downloading, setDownloading] = useState(false);

  const backend = process.env.REACT_APP_BACKEND_URL;
  const imageUrl = `${backend}/api/coupons/${encodeURIComponent(code)}/image`;

  useEffect(() => {
    document.title = `Cupón ${code} · Operación Rescate`;
    (async () => {
      try {
        const { data } = await api.get(`/coupons/${encodeURIComponent(code)}`);
        setCoupon(data);
      } catch (e) {
        setError(e.response?.data?.detail || "Cupón no encontrado");
      } finally {
        setLoading(false);
      }
    })();
  }, [code]);

  const onDownload = async () => {
    setDownloading(true);
    try {
      const res = await fetch(imageUrl);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `cupon-${code}.jpg`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Cupón descargado");
    } catch (e) {
      toast.error("No se pudo descargar");
    } finally {
      setDownloading(false);
    }
  };

  const onShare = async () => {
    try {
      const res = await fetch(imageUrl);
      const blob = await res.blob();
      const file = new File([blob], `cupon-${code}.jpg`, { type: "image/jpeg" });
      if (navigator.share && navigator.canShare?.({ files: [file] })) {
        await navigator.share({
          title: "Mi cupón de Operación Rescate",
          text: `¡Gané un cupón de $15 en Dulce Vida! Código: ${code}`,
          files: [file],
        });
      } else {
        await onDownload();
      }
    } catch (e) {
      if (e?.name !== "AbortError") onDownload();
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black text-yellow-300">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black text-white px-6">
        <div className="text-center max-w-md">
          <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-2">Cupón no válido</h1>
          <p className="text-white/70 mb-6">{error}</p>
          <Button onClick={() => nav("/")} className="bg-yellow-400 hover:bg-yellow-500 text-black font-bold">
            Volver al inicio
          </Button>
        </div>
      </div>
    );
  }

  const expired = coupon?.status === "expired";

  return (
    <div className="min-h-screen bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-yellow-950/30 via-black to-black text-white py-8 px-4">
      <div className="max-w-md mx-auto">
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-6"
        >
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-yellow-400/40 bg-yellow-500/10 text-yellow-300 text-xs font-bold tracking-widest uppercase">
            <Sparkles className="w-3.5 h-3.5" />
            Tu cupón está listo
          </div>
          <h1 className="mt-4 font-black text-3xl text-yellow-200">¡Felicidades!</h1>
          <p className="text-white/60 text-sm mt-1">
            Por inscribirte primero a Operación Rescate
          </p>
        </motion.div>

        {/* Coupon Image */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.1 }}
          className="rounded-3xl overflow-hidden border-2 border-yellow-400/40 shadow-[0_0_40px_-10px_rgba(250,204,21,0.4)] mb-6 bg-black"
          data-testid="coupon-image-wrapper"
        >
          <img
            src={imageUrl}
            alt={`Cupón ${code}`}
            className="w-full block"
            data-testid="coupon-image"
          />
        </motion.div>

        {expired && (
          <div className="mb-4 p-3 rounded-xl bg-red-500/15 border border-red-500/40 text-red-200 text-sm text-center" data-testid="coupon-expired">
            ⚠️ Este cupón ya expiró ({formatDate(coupon?.expires_at)})
          </div>
        )}

        {/* Actions */}
        <div className="space-y-3">
          <Button
            onClick={onShare}
            disabled={downloading || expired}
            data-testid="coupon-share-btn"
            className="w-full bg-yellow-400 hover:bg-yellow-500 text-black font-bold uppercase tracking-wider h-14"
          >
            <Share2 className="w-5 h-5 mr-2" />
            Compartir o Guardar
          </Button>
          <Button
            onClick={onDownload}
            disabled={downloading}
            variant="outline"
            data-testid="coupon-download-btn"
            className="w-full border-yellow-700/40 bg-black/40 text-yellow-200 hover:bg-yellow-500/10 hover:text-yellow-100 h-14 font-bold uppercase tracking-wider"
          >
            {downloading ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <Download className="w-5 h-5 mr-2" />}
            Descargar imagen
          </Button>
        </div>

        {/* Details */}
        {coupon && (
          <div className="mt-8 rounded-2xl border border-yellow-700/30 bg-black/40 p-5 space-y-3">
            <div className="flex items-center gap-2 text-yellow-300 text-sm font-bold uppercase tracking-wider">
              <ShieldCheck className="w-4 h-4" />
              Detalles del cupón
            </div>
            <div className="text-sm text-white/80 space-y-2">
              <div className="flex justify-between"><span className="text-white/50">Código</span><span className="font-mono font-bold text-yellow-200">{coupon.code}</span></div>
              <div className="flex justify-between"><span className="text-white/50">Valor</span><span className="font-bold">${coupon.value}</span></div>
              <div className="flex justify-between"><span className="text-white/50">Para</span><span className="font-medium text-right">{coupon.full_name}</span></div>
              {coupon.camper_number_display && (
                <div className="flex justify-between"><span className="text-white/50">Campista</span><span className="font-mono text-yellow-300">{coupon.camper_number_display}</span></div>
              )}
              <div className="flex justify-between items-center"><span className="text-white/50 flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> Válido hasta</span><span className="font-medium">{formatDate(coupon.expires_at)}</span></div>
            </div>
          </div>
        )}

        <div className="mt-6 text-center text-white/40 text-xs">
          <p>Muéstrale este cupón al equipo de <span className="text-pink-300 font-bold">Dulce Vida</span></p>
          <p className="mt-1">para reclamar tu premio antes de la fecha de vencimiento.</p>
        </div>
      </div>
    </div>
  );
}
